import { styled } from "styled-components";

import { CrossIcon, SectionTitleUnderlined, UIButton } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { AddCustomContent } from "domains/customContentAndSources/components/AddCustomContent";
import {
  getCustomContentDeleteError,
  getCustomContentIsDeleting,
  getCustomContentIsUploading,
  getCustomContentUploadError,
  getInProgressCustomContentId,
  getIsNewCustomContent,
} from "domains/customContentAndSources/selectors";
import { useEffectAfterFirstRender } from "domains/topicDetail/components/advancedOptions/useEffectAfterFirstRender";

const StyledContainer = styled.div`
  overflow-y: auto;
  padding-right: 15px;
`;

const StyledClearButtonIcon = styled(CrossIcon)`
  width: 14px;
  height: 14px;
  cursor: pointer;
  transition: all 0.3s;
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.textFade};
  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

type AddCustomContentPanelProps = { toggleVisibility: () => void };

export const AddCustomContentPanel = ({ toggleVisibility }: AddCustomContentPanelProps) => {
  const isUploading = useSelector(getCustomContentIsUploading);
  const uploadError = useSelector(getCustomContentUploadError);
  const inProgressCustomContentId = useSelector(getInProgressCustomContentId);
  const isDeleting = useSelector(getCustomContentIsDeleting(inProgressCustomContentId));
  const deleteError = useSelector(getCustomContentDeleteError(inProgressCustomContentId));
  const isNewCustomContent = useSelector(getIsNewCustomContent);
  const translate = useTranslate();

  useEffectAfterFirstRender(() => {
    if (!isUploading && !uploadError) {
      toggleVisibility();
    }
  }, [isUploading, uploadError]);

  useEffectAfterFirstRender(() => {
    if (!isDeleting && !deleteError) {
      toggleVisibility();
    }
  }, [isDeleting, deleteError]);

  const title = isNewCustomContent
    ? "app.custom_attachment.section.add.title"
    : "app.custom_attachment.section.edit.title";

  return (
    <StyledContainer>
      <SectionTitleUnderlined rightElement={<StyledClearButtonIcon onClick={toggleVisibility} />}>
        {translate(title)}
      </SectionTitleUnderlined>
      <AddCustomContent />
      <UIButton variant="outlined" onClick={toggleVisibility} style={{ margin: "1rem 0" }} fullwidth>
        {translate("app.create.create_map.cancel")}
      </UIButton>
    </StyledContainer>
  );
};
